import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { accountsAPI } from '@/lib/api';
import { useProfile } from '@/lib/profile-context';
import { useAuth } from '@/lib/auth-context';
import { defaultHomePathForUser } from '@/lib/plan-access';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Plus, ExternalLink, Store, Calendar, Shield, Loader2, 
  ChevronRight, Globe, Mail, CheckCircle, AlertCircle, 
  Image, ChevronLeft, ChevronRight as ChevronRightIcon 
} from 'lucide-react';

function AddAccountModal({ open, onOpenChange, onConnect }) {
  const [marketplaces, setMarketplaces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    account_name: '',
    marketplace_id: '',
    business_type: '',
    fulfillment_model: { fba: false, fbm: false },
    brand_registry: false,
    primary_contact_name: '',
    primary_contact_email: '',
  });

  useEffect(() => {
    if (open) {
      accountsAPI.getMarketplaces()
        .then(res => {
          const data = res.data?.data || res.data || [];
          setMarketplaces(Array.isArray(data) ? data : []);
        })
        .catch(() => setMarketplaces([]));
    }
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.account_name.trim()) {
      toast.error('Account name is required');
      return;
    }
    if (!formData.marketplace_id) {
      toast.error('Please select a marketplace');
      return;
    }
    setLoading(true);
    try {
      const payload = { ...formData };
      if (!payload.business_type) delete payload.business_type;
      const res = await accountsAPI.startSpConnect(payload);
      const data = res.data || res;
      if (data.redirect_url) {
        window.location.href = data.redirect_url;
      } else {
        toast.error('No redirect URL received from server');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to start Amazon connection');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      account_name: '', marketplace_id: '', business_type: '',
      fulfillment_model: { fba: false, fbm: false }, brand_registry: false,
      primary_contact_name: '', primary_contact_email: '',
    });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) resetForm(); onOpenChange(v); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Store className="w-5 h-5 text-[hsl(188,100%,55%)]" />
            Connect Amazon Account
          </DialogTitle>
          <DialogDescription>
            Connect your Amazon Seller Central account via SP API to enable enforcement monitoring.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="space-y-2">
            <Label htmlFor="account_name">Account Name *</Label>
            <Input id="account_name" placeholder="e.g. My Store" value={formData.account_name}
              onChange={e => setFormData(p => ({ ...p, account_name: e.target.value }))} required />
          </div>

          <div className="space-y-2">
            <Label>Marketplace *</Label>
            <Select value={formData.marketplace_id}
              onValueChange={v => setFormData(p => ({ ...p, marketplace_id: v }))}>
              <SelectTrigger><SelectValue placeholder="Select marketplace" /></SelectTrigger>
              <SelectContent>
                {marketplaces.map(m => (
                  <SelectItem key={m.id} value={m.id}>{m.name} ({m.countryCode})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Business Type</Label>
            <Select value={formData.business_type}
              onValueChange={v => setFormData(p => ({ ...p, business_type: v }))}>
              <SelectTrigger><SelectValue placeholder="Select (optional)" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Manufacturer">Manufacturer</SelectItem>
                <SelectItem value="Brand Owner">Brand Owner</SelectItem>
                <SelectItem value="Reseller">Reseller</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Fulfillment Model</Label>
            <div className="flex gap-6">
              <div className="flex items-center gap-2">
                <Checkbox id="fba" checked={formData.fulfillment_model.fba}
                  onCheckedChange={v => setFormData(p => ({ ...p, fulfillment_model: { ...p.fulfillment_model, fba: v } }))} />
                <Label htmlFor="fba" className="font-normal">FBA</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="fbm" checked={formData.fulfillment_model.fbm}
                  onCheckedChange={v => setFormData(p => ({ ...p, fulfillment_model: { ...p.fulfillment_model, fbm: v } }))} />
                <Label htmlFor="fbm" className="font-normal">FBM</Label>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox id="brand_registry" checked={formData.brand_registry}
              onCheckedChange={v => setFormData(p => ({ ...p, brand_registry: v }))} />
            <Label htmlFor="brand_registry" className="font-normal">Brand Registry</Label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="contact_name">Primary Contact Name</Label>
              <Input id="contact_name" placeholder="John Doe" value={formData.primary_contact_name}
                onChange={e => setFormData(p => ({ ...p, primary_contact_name: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact_email">Primary Contact Email</Label>
              <Input id="contact_email" type="email" placeholder="john@example.com" value={formData.primary_contact_email}
                onChange={e => setFormData(p => ({ ...p, primary_contact_email: e.target.value }))} />
            </div>
          </div>

          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={loading} className="grad-accent text-white border-0 gap-2">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
              {loading ? 'Connecting...' : 'Connect with Amazon'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Image Gallery Component for screenshots
function ImageGallery({ images, currentIndex, onClose }) {
  const [index, setIndex] = useState(currentIndex);

  const nextImage = () => {
    setIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] p-0 overflow-hidden">
        <div className="relative">
          <img 
            src={images[index]} 
            alt={`Screenshot ${index + 1}`}
            className="w-full h-auto max-h-[80vh] object-contain"
          />
          {images.length > 1 && (
            <>
              <Button
                size="sm"
                variant="outline"
                className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full p-1 h-8 w-8"
                onClick={prevImage}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 h-8 w-8"
                onClick={nextImage}
              >
                <ChevronRightIcon className="w-4 h-4" />
              </Button>
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/50 text-white text-xs px-2 py-1 rounded">
                {index + 1} / {images.length}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AccountsPage() {
  const { canConnectAmazon, user } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [grantAccessModalOpen, setGrantAccessModalOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedSupportStatus, setSelectedSupportStatus] = useState('pending');
  const [supportMarketplace, setSupportMarketplace] = useState('');
  const [supportCountry, setSupportCountry] = useState('');
  const [invitationSent, setInvitationSent] = useState(false);
  const [updatingSupportAccess, setUpdatingSupportAccess] = useState(false);
  const [showImageGallery, setShowImageGallery] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { selectProfile, accountId: currentAccountId } = useProfile();

  const [expandedAccountId, setExpandedAccountId] = useState(null);
  const [accountProfiles, setAccountProfiles] = useState({});
  const [loadingProfiles, setLoadingProfiles] = useState({});

  // Screenshots for the guide
  const guideScreenshots = [
    '/screenshots/img1.png', // Step 1-2
    '/screenshots/img2.png', // Step 3
    '/screenshots/img3.png', // Step 4
    '/screenshots/img4.png', // Business name reference
    '/screenshots/img5.png',
    '/screenshots/img6.png', 
    '/screenshots/img7.png',    
  ];

  const fetchAccounts = useCallback(async () => {
    try {
      setLoading(true);
      const res = await accountsAPI.getAll();
      const data = res.data?.data || res.data || [];
      setAccounts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load accounts:', err);
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  useEffect(() => {
    if (searchParams.get('connected') === 'true') {
      toast.success('Amazon account connected successfully!');
      fetchAccounts();
      window.history.replaceState({}, '', '/accounts');
    }
  }, [searchParams, fetchAccounts]);

  const handleAccountClick = async (accountId) => {
    if (expandedAccountId === accountId) {
      setExpandedAccountId(null);
      return;
    }
    setExpandedAccountId(accountId);
    if (!accountProfiles[accountId]) {
      setLoadingProfiles(prev => ({ ...prev, [accountId]: true }));
      try {
        const res = await accountsAPI.getProfiles(accountId);
        const data = res.data?.data || res.data || [];
        setAccountProfiles(prev => ({ ...prev, [accountId]: Array.isArray(data) ? data : [] }));
      } catch {
        setAccountProfiles(prev => ({ ...prev, [accountId]: [] }));
      } finally {
        setLoadingProfiles(prev => ({ ...prev, [accountId]: false }));
      }
    }
  };

  const handleProfileSelect = (accountId, profileId) => {
    selectProfile(String(accountId), String(profileId));
    const home = defaultHomePathForUser(user);
    toast.success('Profile selected');
    navigate(home);
  };

  const openGrantAccessModal = (account) => {
    setSelectedAccount(account);
    setSelectedSupportStatus(account.support_access_status || 'pending');
    setSupportMarketplace(account.support_marketplace || '');
    setSupportCountry(account.support_country || '');
    setInvitationSent(false);
    setGrantAccessModalOpen(true);
  };

  const handleConfirmSupportAccess = async () => {
    if (!selectedAccount) return;

    try {
      setUpdatingSupportAccess(true);
      const res = await accountsAPI.updateSupportAccess(selectedAccount.id, {
        support_access_status: selectedSupportStatus,
        support_marketplace: supportMarketplace,
        support_country: supportCountry,
      });
      const scrapeStarted = Boolean(res.data?.scrape_started);
      const statusLabel = selectedSupportStatus === 'granted'
        ? (scrapeStarted
          ? 'Access granted — scrape started. Notices will appear in a few minutes.'
          : 'Access granted! Our system can now scrape your Amazon data.')
        : selectedSupportStatus === 'pending'
          ? 'Access marked as pending. We\'ll wait for your confirmation.'
          : 'Access access removed.';
      toast.success(statusLabel);
      setGrantAccessModalOpen(false);
      setSelectedAccount(null);
      setSelectedSupportStatus('pending');
      setSupportMarketplace('');
      setSupportCountry('');
      setInvitationSent(false);
      fetchAccounts();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update support access');
    } finally {
      setUpdatingSupportAccess(false);
    }
  };

  const getSupportAccessBadge = (status) => {
    switch (status) {
      case 'pending':
        return (
          <Badge variant="outline" className="border-amber-500/30 text-amber-400 bg-amber-500/10 text-[10px] px-1.5 py-0 gap-1">
            <AlertCircle className="w-2.5 h-2.5" />
            Pending Access
          </Badge>
        );
      case 'granted':
        return (
          <Badge variant="outline" className="border-emerald-500/30 text-emerald-400 bg-emerald-500/10 text-[10px] px-1.5 py-0 gap-1">
            <CheckCircle className="w-2.5 h-2.5" />
            Access Granted
          </Badge>
        );
      default:
        return (
          <Badge variant="outline" className="text-[10px] px-1.5 py-0">
            No Access
          </Badge>
        );
    }
  };

  const getGrantAccessButtonClass = (status) => {
    switch (status) {
      case 'granted':
        return 'border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10';
      case 'pending':
        return 'border-amber-500/40 text-amber-400 hover:bg-amber-500/10';
      default:
        return 'border-border text-foreground hover:bg-muted';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{canConnectAmazon ? 'Amazon Accounts' : 'Your Brands'}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {canConnectAmazon
              ? 'Select an account and profile to start monitoring enforcement risks'
              : 'Select a brand profile to view dashboard, signals, and metrics'}
          </p>
        </div>
        {canConnectAmazon && (
          <Button onClick={() => setModalOpen(true)} className="grad-accent text-white border-0 gap-2">
            <Plus className="w-4 h-4" />
            Add Account
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : accounts.length === 0 ? (
        <div className="border border-border/60 rounded-xl bg-card p-12 text-center">
          <Store className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-40" />
          <h3 className="text-lg font-medium mb-2">{canConnectAmazon ? 'No accounts connected' : 'No brands assigned'}</h3>
          <p className="text-sm text-muted-foreground mb-6 max-w-sm mx-auto">
            {canConnectAmazon
              ? 'Connect your Amazon Seller Central account to start monitoring enforcement risks.'
              : 'Ask your admin to assign brand profiles to your manager account.'}
          </p>
          {canConnectAmazon && (
            <Button onClick={() => setModalOpen(true)} className="grad-accent text-white border-0 gap-2">
              <Plus className="w-4 h-4" />
              Connect Your First Account
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {accounts.map((account) => {
            const isExpanded = expandedAccountId === account.id;
            const profiles = accountProfiles[account.id] || [];
            const isLoadingProfiles = loadingProfiles[account.id];
            const isActive = String(currentAccountId) === String(account.id);

            return (
              <div key={account.id} className={`border rounded-xl bg-card overflow-hidden transition-colors ${isActive ? 'border-[hsl(188,100%,55%)]/40' : 'border-border/60'}`}>
                <button
                  onClick={() => handleAccountClick(account.id)}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/20 transition-colors text-left"
                >
                  <div className="flex items-center gap-3">
                    <Store className="w-5 h-5 text-[hsl(188,100%,55%)]" />
                    <div>
                      <span className="font-medium">{account.accountName || account.name}</span>
                      <div className="flex items-center gap-3 mt-0.5">
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {account.created_at ? new Date(account.created_at).toLocaleDateString() : '-'}
                        </span>
                        {(account.authConnected === 'Yes' || account.status === 'active') && (
                          <Badge variant="outline" className="border-emerald-500/30 text-emerald-400 bg-emerald-500/10 text-[10px] px-1.5 py-0">
                            Connected
                          </Badge>
                        )}
                        {getSupportAccessBadge(account.support_access_status)}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {canConnectAmazon && (
                      <Button
                        size="sm"
                        variant="outline"
                        className={getGrantAccessButtonClass(account.support_access_status)}
                        onClick={(e) => {
                          e.stopPropagation();
                          openGrantAccessModal(account);
                        }}
                      >
                        Grant Access
                      </Button>
                    )}
                    <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                  </div>
                </button>

                {isExpanded && (
                  <div className="border-t border-border/40 px-4 py-3 bg-muted/10">
                    {isLoadingProfiles ? (
                      <div className="flex items-center gap-2 py-4 justify-center text-sm text-muted-foreground">
                        <Loader2 className="w-4 h-4 animate-spin" /> Loading profiles...
                      </div>
                    ) : profiles.length === 0 ? (
                      <p className="text-sm text-muted-foreground py-4 text-center">
                        No profiles found for this account. Complete the Amazon SP API connection first.
                      </p>
                    ) : (
                      <div className="space-y-2">
                        <p className="text-xs text-muted-foreground font-medium mb-2">Select a profile to monitor:</p>
                        {profiles.map((profile) => (
                          <button
                            key={profile.id}
                            onClick={() => handleProfileSelect(account.id, profile.id)}
                            className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg border border-border/40 hover:border-[hsl(188,100%,55%)]/40 hover:bg-muted/20 transition-colors text-left"
                          >
                            <div className="flex items-center gap-2.5">
                              <Globe className="w-4 h-4 text-[hsl(188,100%,55%)]" />
                              <div>
                                <span className="text-sm font-medium">{profile.name || `Profile ${profile.id}`}</span>
                                <div className="flex items-center gap-2 mt-0.5">
                                  {profile.countryCode && (
                                    <span className="text-xs text-muted-foreground">{profile.countryCode}</span>
                                  )}
                                  {profile.type && (
                                    <span className="text-xs text-muted-foreground capitalize">{profile.type}</span>
                                  )}
                                  {profile.sellerId && (
                                    <span className="text-xs text-muted-foreground">Seller: {profile.sellerId}</span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {canConnectAmazon && <AddAccountModal open={modalOpen} onOpenChange={setModalOpen} />}

      {/* Updated Grant Access Modal with Images */}
      <Dialog open={grantAccessModalOpen} onOpenChange={setGrantAccessModalOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-[hsl(188,100%,55%)]" />
              Grant Amazon Access
            </DialogTitle>
            <DialogDescription>
              To monitor your account, we need access to your Amazon Seller Central data.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Two-column layout for instructions and account info */}
            <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
              {/* Left column - Instructions with images */}
              <div className="rounded-lg bg-blue-500/5 border border-blue-500/20 p-3">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-semibold flex items-center gap-1">
                    <Mail className="w-3.5 h-3.5 text-blue-400" />
                    Quick Steps
                  </h4>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-[10px] gap-1"
                    onClick={() => {
                      setCurrentImageIndex(0);
                      setShowImageGallery(true);
                    }}
                  >
                    <Image className="w-3 h-3" />
                    View Screenshots
                  </Button>
                </div>
                <ol className="space-y-1.5 text-xs">
                  <li className="flex gap-1.5">
                    <span className="flex-shrink-0 w-4 h-4 rounded-full bg-blue-500/20 text-blue-400 text-[10px] flex items-center justify-center font-bold">1</span>
                    <span>Login to Amazon Seller Central</span>
                  </li>
                  <li className="flex gap-1.5">
                    <span className="flex-shrink-0 w-4 h-4 rounded-full bg-blue-500/20 text-blue-400 text-[10px] flex items-center justify-center font-bold">2</span>
                    <span>Go to Settings → User Permissions</span>
                  </li>
                  <li className="flex gap-1.5">
                    <span className="flex-shrink-0 w-4 h-4 rounded-full bg-blue-500/20 text-blue-400 text-[10px] flex items-center justify-center font-bold">3</span>
                    <span>Click "Add a New User"</span>
                  </li>
                  <li className="flex gap-1.5">
                    <span className="flex-shrink-0 w-4 h-4 rounded-full bg-blue-500/20 text-blue-400 text-[10px] flex items-center justify-center font-bold">4</span>
                    <span>Email: <code className="px-1 py-0 bg-muted rounded text-[10px] font-mono">selleraccess2026@gmail.com</code></span>
                  </li>
                  <li className="flex gap-1.5">
                    <span className="flex-shrink-0 w-4 h-4 rounded-full bg-blue-500/20 text-blue-400 text-[10px] flex items-center justify-center font-bold">5</span>
                    <span>Click "Send Invitation" & click on the user's email to open permission  pag</span>
                  </li>
                  <li className="flex gap-1.5">
                    <span className="flex-shrink-0 w-4 h-4 rounded-full bg-blue-500/20 text-blue-400 text-[10px] flex items-center justify-center font-bold">7</span>
                    <span>Select <strong className="text-blue-400">"Performance"</strong> permission → Choose <strong>"View"</strong> access</span>
                  </li>
                  <li className="flex gap-1.5">
                    <span className="flex-shrink-0 w-4 h-4 rounded-full bg-blue-500/20 text-blue-400 text-[10px] flex items-center justify-center font-bold">8</span>
                    <span>Select <strong className="text-blue-400">"Reports"</strong> permission → Choose <strong>"View"</strong> access</span>
                  </li>
                  <li className="flex gap-1.5">
                    <span className="flex-shrink-0 w-4 h-4 rounded-full bg-blue-500/20 text-blue-400 text-[10px] flex items-center justify-center font-bold">9</span>
                    <span>Click <strong>"Save Changes"</strong> or <strong>"Continue"</strong> to confirm permissions</span>
                  </li>
                </ol>

                {/* Mini screenshot previews */}
                <div className="mt-3 pt-2 border-t border-blue-500/20">
                  <p className="text-[10px] text-muted-foreground mb-1">Need help? Click any image to enlarge:</p>
                  <div className="flex gap-1.5">
                    {guideScreenshots.map((screenshot, idx) => (
                      <button
                        key={idx}
                        onClick={() => {
                          setCurrentImageIndex(idx);
                          setShowImageGallery(true);
                        }}
                        className="relative group"
                      >
                        <div className="w-12 h-12 rounded bg-muted/50 border border-border/40 overflow-hidden hover:border-blue-500/50 transition-colors">
                          <img 
                            src={screenshot} 
                            alt={`Step ${idx + 1}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.src = '/screenshots/placeholder.png';
                            }}
                          />
                        </div>
                        <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-blue-500 text-white text-[8px] flex items-center justify-center font-bold">
                          {idx + 1}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right column - Account info */}
              {/* <div className="rounded-md border border-border/60 bg-muted/20 p-3">
                <p className="text-xs text-muted-foreground mb-1">Account:</p>
                <p className="text-sm font-medium">{selectedAccount?.accountName || selectedAccount?.name || '—'}</p>
              </div> */}
            </div>

            {/* Business name and country - compact layout */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="support_marketplace" className="text-xs">
                  Business Name <span className="text-red-400">*</span>
                </Label>
                <Input
                  id="support_marketplace"
                  value={supportMarketplace}
                  onChange={(e) => setSupportMarketplace(e.target.value)}
                  placeholder="e.g., AAA Sports Apparel"
                  className="h-9 text-sm"
                />
                <p className="text-[10px] text-muted-foreground">
                  Exactly as shown in Amazon account switcher
                </p>
              </div>

              <div className="space-y-1">
                <Label htmlFor="support_country" className="text-xs">
                  Country Name <span className="text-red-400">*</span>
                </Label>
                <Input
                  id="support_country"
                  value={supportCountry}
                  onChange={(e) => setSupportCountry(e.target.value)}
                  placeholder="e.g., United States"
                  className="h-9 text-sm"
                />
                <p className="text-[10px] text-muted-foreground">
                  Exactly as shown in Amazon account switcher
                </p>
              </div>
            </div>

            {/* Help text with link to example */}
            <div className="rounded-md bg-muted/20 p-2">
              <p className="text-[10px] text-muted-foreground flex items-center gap-1">
                <Globe className="w-3 h-3" />
                Not sure what to enter? 
                <button 
                  onClick={() => {
                    setCurrentImageIndex(4);
                    setShowImageGallery(true);
                  }}
                  className="text-blue-400 hover:underline"
                >
                  View example
                </button>
              </p>
            </div>

            {/* Status and confirmation in one row */}
            <div className="grid grid-cols-2 gap-3 items-start">
              <div className="space-y-1">
                <Label className="text-xs">Access Status</Label>
                <Select value={selectedSupportStatus} onValueChange={setSelectedSupportStatus}>
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">⏳ Pending Access</SelectItem>
                    <SelectItem value="granted">✅ Access Granted</SelectItem>
                    <SelectItem value="not_requested">❌ No Access</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {selectedSupportStatus === 'pending' && (
                <div className="flex items-start gap-2 pt-5">
                  <Checkbox
                    id="sent-invitation"
                    checked={invitationSent}
                    onCheckedChange={(value) => setInvitationSent(Boolean(value))}
                    className="mt-0.5"
                  />
                  <Label htmlFor="sent-invitation" className="font-normal text-xs cursor-pointer leading-tight">
                    I have sent the invitation
                  </Label>
                </div>
              )}
            </div>

            {/* Compact alert messages */}
            {selectedSupportStatus === 'granted' && (
              <div className="rounded-md bg-emerald-500/5 border border-emerald-500/20 p-2">
                <p className="text-xs text-emerald-400 flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" />
                  Our system will now automatically scrape your Amazon data.
                </p>
              </div>
            )}

            {(!supportMarketplace || !supportCountry) && selectedSupportStatus !== 'not_requested' && (
              <div className="rounded-md bg-amber-500/5 border border-amber-500/20 p-2">
                <p className="text-xs text-amber-400 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Enter business name and country for automatic marketplace selection.
                </p>
              </div>
            )}
          </div>

          <DialogFooter className="mt-4">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setGrantAccessModalOpen(false);
                setSelectedAccount(null);
                setSelectedSupportStatus('pending');
                setSupportMarketplace('');
                setSupportCountry('');
                setInvitationSent(false);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleConfirmSupportAccess}
              disabled={
                (selectedSupportStatus === 'pending' && !invitationSent) ||
                (selectedSupportStatus !== 'not_requested' && (!supportMarketplace.trim() || !supportCountry.trim())) ||
                updatingSupportAccess
              }
              className="gap-1"
            >
              {updatingSupportAccess ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Gallery Dialog */}
      {showImageGallery && (
        <ImageGallery 
          images={guideScreenshots}
          currentIndex={currentImageIndex}
          onClose={() => setShowImageGallery(false)}
        />
      )}
    </div>
  );
}